import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Trash2, Package, AlertTriangle, Eye, TrendingUp, History, ArrowLeft, Building2, Upload, Download, CheckCircle, XCircle, FileSpreadsheet, ArrowRightLeft } from 'lucide-react';
import apiService from '../services/apiService';
import { useAuth } from '../context/AuthContext';

const ProductManagement = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [currentView, setCurrentView] = useState('list');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [stockMovements, setStockMovements] = useState([]);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Company state
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProduct = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const { user } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    price: '',
    cost: '',
    quantityInStock: '',
    quantityInShop: '',
    category: '',
    unit: '',
    active: true,
    companyId: ''
  });

  // Stock replenishment form
  const [stockForm, setStockForm] = useState({
    quantity: '',
    quantityToShop: '',
    replenishDate: new Date().toISOString().split('T')[0],
    notes: ''
  });

  // Transfer to shop form
  const [transferForm, setTransferForm] = useState({
    quantity: '',
    notes: ''
  });

  // Bulk upload state
  const [bulkData, setBulkData] = useState([]);
  const [bulkErrors, setBulkErrors] = useState([]);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 });
  const [bulkFileName, setBulkFileName] = useState('');

  // Bulk restock state
  const [restockData, setRestockData] = useState([]);
  const [restockImporting, setRestockImporting] = useState(false);
  const [restockProgress, setRestockProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 });
  const [restockFileName, setRestockFileName] = useState('');

  // Bulk transfer state
  const [transferData, setTransferData] = useState([]);
  const [transferImporting, setTransferImporting] = useState(false);
  const [transferProgress, setTransferProgress] = useState({ current: 0, total: 0, success: 0, failed: 0 });
  const [transferFileName, setTransferFileName] = useState('');

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Reload data when company changes
  useEffect(() => {
    if (selectedCompanyId) {
      loadProducts();
      loadCategories();
    }
  }, [selectedCompanyId]);

  // Filter products when search term or category changes
  useEffect(() => {
    filterProducts();
  }, [products, searchTerm, selectedCategory]);

  const loadCompanies = async () => {
    try {
      const data = await apiService.getAllCompanies();
      setCompanies(data);
      const savedCompanyId = localStorage.getItem('selectedCompanyId');
      if (savedCompanyId && data.find(c => c.id === parseInt(savedCompanyId))) {
        setSelectedCompanyId(savedCompanyId);
      } else if (data.length > 0) {
        setSelectedCompanyId(data[0].id.toString());
        localStorage.setItem('selectedCompanyId', data[0].id.toString());
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
      setLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const companyId = e.target.value;
    setSelectedCompanyId(companyId);
    localStorage.setItem('selectedCompanyId', companyId);
    setCurrentPage(1);
  };

  const loadProducts = async () => {
    try {
      setLoading(true);
      apiService.refreshTokenFromStorage();
      const data = selectedCompanyId
        ? await apiService.getProductsByCompany(selectedCompanyId)
        : await apiService.getAllProducts();
      setProducts(data);
    } catch (error) {
      setError('Failed to load products');
      console.error('Error loading products:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      apiService.refreshTokenFromStorage();
      const data = selectedCompanyId
        ? await apiService.getCategoriesByCompany(selectedCompanyId)
        : await apiService.getAllCategories();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadStockMovements = async (productId) => {
    try {
      const data = await apiService.getProductStockMovements(productId);
      setStockMovements(data);
    } catch (error) {
      console.error('Error loading stock movements:', error);
      setStockMovements([]);
    }
  };

  const filterProducts = () => {
    let filtered = [...products];

    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    setFilteredProducts(filtered);
  };

  const handleCreate = () => {
    setCurrentView('create');
    setSelectedProduct(null);
    setFormData({
      name: '',
      description: '',
      sku: '',
      price: '',
      cost: '',
      quantityInStock: '',
      quantityInShop: '',
      category: '',
      unit: '',
      active: true,
      companyId: selectedCompanyId
    });
    setError('');
  };

  const handleEdit = (product) => {
    setCurrentView('edit');
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      sku: product.sku,
      price: product.price.toString(),
      cost: product.cost.toString(),
      quantityInStock: product.quantityInStock?.toString() || '',
      quantityInShop: product.quantityInShop?.toString() || '0',
      category: product.category || '',
      unit: product.unit || '',
      active: product.active,
      companyId: product.companyId || selectedCompanyId
    });
    setError('');
  };

  const handleView = (product) => {
    setCurrentView('view');
    setSelectedProduct(product);
  };

  const handleReplenishStock = (product) => {
    setSelectedProduct(product);
    setCurrentView('stock');
    setStockForm({
      quantity: '',
      quantityToShop: '',
      replenishDate: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setError('');
  };

  const handleTransferToShop = (product) => {
    setSelectedProduct(product);
    setCurrentView('transfer');
    setTransferForm({ quantity: '', notes: '' });
    setError('');
  };

  const handleViewMovements = async (product) => {
    setSelectedProduct(product);
    setCurrentView('movements');
    await loadStockMovements(product.id);
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedProduct(null);
    setError('');
    setStockMovements([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        cost: parseFloat(formData.cost),
        quantityInStock: formData.quantityInStock ? parseInt(formData.quantityInStock) : 0,
        quantityInShop: formData.quantityInShop ? parseInt(formData.quantityInShop) : 0,
        companyId: parseInt(formData.companyId || selectedCompanyId)
      };

      if (currentView === 'create') {
        await apiService.createProduct(productData);
      } else {
        await apiService.updateProduct(selectedProduct.id, productData);
      }

      loadProducts();
      handleBackToList();
    } catch (error) {
      setError(error.message || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStockReplenishment = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    
    try {
      const replenishmentData = {
        quantity: parseInt(stockForm.quantity),
        quantityToShop: stockForm.quantityToShop ? parseInt(stockForm.quantityToShop) : 0,
        restockDate: stockForm.replenishDate + 'T00:00:00',
        notes: stockForm.notes,
        companyId: parseInt(selectedCompanyId)
      };

      await apiService.replenishProductStock(selectedProduct.id, replenishmentData);
      loadProducts();
      handleBackToList();
    } catch (error) {
      setError(error.message || 'Failed to replenish stock');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitTransfer = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await apiService.transferToShop(selectedProduct.id, {
        quantity: parseInt(transferForm.quantity),
        notes: transferForm.notes || 'Transfer from store to shop',
        companyId: parseInt(selectedCompanyId)
      });
      loadProducts();
      handleBackToList();
    } catch (error) {
      setError(error.message || 'Failed to transfer stock');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await apiService.deleteProduct(productId);
        loadProducts();
      } catch (error) {
        setError('Failed to delete product');
      }
    }
  };

  const getStockStatus = (quantity) => {
    if (quantity === 0) return { label: 'Out of Stock', color: 'text-red-600 bg-red-100' };
    if (quantity <= 10) return { label: 'Low Stock', color: 'text-yellow-600 bg-yellow-100' };
    return { label: 'In Stock', color: 'text-green-600 bg-green-100' };
  };

  const getMovementTypeColor = (type) => {
    const colors = {
      'SALE': 'text-red-600 bg-red-100',
      'PURCHASE': 'text-green-600 bg-green-100',
      'ADJUSTMENT': 'text-blue-600 bg-blue-100',
      'RETURN': 'text-yellow-600 bg-yellow-100',
      'DAMAGE': 'text-gray-600 bg-gray-100',
      'TRANSFER_TO_SHOP': 'text-teal-600 bg-teal-100'
    };
    return colors[type] || 'text-gray-600 bg-gray-100';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString();
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === parseInt(companyId));
    return company ? company.name : '';
  };

  // ============================
  // BULK UPLOAD FUNCTIONS
  // ============================
  const downloadTemplate = () => {
    const headers = ['name', 'sku', 'description', 'category', 'price', 'cost', 'quantityInStock', 'quantityInShop', 'unit'];
    const sampleRows = [
      ['Sample Product', 'SKU001', 'Product description here', 'Electronics', '1500.00', '1000.00', '50', '20', 'pcs'],
      ['Another Product', 'SKU002', 'Another description', 'Accessories', '250.00', '150.00', '100', '30', 'pcs'],
    ];
    const csvContent = [
      headers.join(','),
      ...sampleRows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const companyName = companies.find(c => c.id === parseInt(selectedCompanyId))?.name || 'company';
    a.href = url;
    a.download = `product_template_${companyName.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseCSV = (text) => {
    const lines = text.split(/\r?\n/).filter(line => line.trim());
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, '').toLowerCase());
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = '';
      let inQuotes = false;

      for (let c = 0; c < lines[i].length; c++) {
        if (lines[i][c] === '"') {
          inQuotes = !inQuotes;
        } else if (lines[i][c] === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += lines[i][c];
        }
      }
      values.push(current.trim());

      const row = {};
      headers.forEach((header, idx) => {
        row[header] = values[idx] || '';
      });
      row._rowNum = i + 1;
      rows.push(row);
    }
    return rows;
  };

  const validateBulkData = (rows) => {
    const errors = [];
    const existingSkus = products.map(p => p.sku?.toLowerCase()).filter(Boolean);
    const uploadSkus = [];

    rows.forEach((row, index) => {
      const rowErrors = [];

      if (!row.name?.trim()) {
        rowErrors.push('Name is required');
      }
      if (!row.sku?.trim()) {
        rowErrors.push('SKU is required');
      } else {
        if (existingSkus.includes(row.sku.trim().toLowerCase())) {
          rowErrors.push('SKU already exists in system');
        }
        if (uploadSkus.includes(row.sku.trim().toLowerCase())) {
          rowErrors.push('Duplicate SKU in file');
        }
        uploadSkus.push(row.sku.trim().toLowerCase());
      }
      if (!row.price || isNaN(parseFloat(row.price)) || parseFloat(row.price) < 0) {
        rowErrors.push('Valid price is required');
      }
      if (row.cost && (isNaN(parseFloat(row.cost)) || parseFloat(row.cost) < 0)) {
        rowErrors.push('Cost must be a valid number');
      }
      if (row.quantityinstock && (isNaN(parseInt(row.quantityinstock)) || parseInt(row.quantityinstock) < 0)) {
        rowErrors.push('Quantity must be a valid number');
      }
      // Validate quantityInShop
      const shopQty = parseInt(row.quantityinshop || row.quantityInShop || 0) || 0;
      const totalQty = parseInt(row.quantityinstock || row.quantityInStock || 0) || 0;
      if (shopQty < 0) {
        rowErrors.push('Qty in shop cannot be negative');
      } else if (shopQty > totalQty) {
        rowErrors.push('Qty in shop cannot exceed total quantity');
      }

      row._errors = rowErrors;
      row._valid = rowErrors.length === 0;
      row._index = index;
    });

    return rows;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkFileName(file.name);
    setError('');

    const isCSV = file.name.toLowerCase().endsWith('.csv');
    const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');

    if (!isCSV && !isExcel) {
      setError('Please upload a CSV or Excel (.xlsx) file');
      return;
    }

    if (isCSV) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const rows = parseCSV(event.target.result);
          if (rows.length === 0) {
            setError('No data rows found in file');
            return;
          }
          const validated = validateBulkData(rows);
          setBulkData(validated);
          setBulkErrors(validated.filter(r => !r._valid));
          setCurrentView('bulkPreview');
        } catch (err) {
          setError('Failed to parse CSV file: ' + err.message);
        }
      };
      reader.readAsText(file);
    } else if (isExcel) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const XLSX = await import('xlsx');
          const workbook = XLSX.read(event.target.result, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

          // Normalize headers to lowercase
          const rows = jsonData.map((row, i) => {
            const normalized = {};
            Object.keys(row).forEach(key => {
              normalized[key.toLowerCase().trim()] = String(row[key]);
            });
            normalized._rowNum = i + 2;
            return normalized;
          });

          if (rows.length === 0) {
            setError('No data rows found in file');
            return;
          }
          const validated = validateBulkData(rows);
          setBulkData(validated);
          setBulkErrors(validated.filter(r => !r._valid));
          setCurrentView('bulkPreview');
        } catch (err) {
          setError('Failed to parse Excel file. Make sure "xlsx" package is installed: npm install xlsx');
          console.error(err);
        }
      };
      reader.readAsArrayBuffer(file);
    }

    // Reset file input
    e.target.value = '';
  };

  const handleRemoveBulkRow = (index) => {
    const updated = bulkData.filter((_, i) => i !== index);
    const revalidated = validateBulkData(updated);
    setBulkData(revalidated);
    setBulkErrors(revalidated.filter(r => !r._valid));
  };

  const handleBulkImport = async () => {
    const validRows = bulkData.filter(r => r._valid);
    if (validRows.length === 0) {
      setError('No valid rows to import');
      return;
    }

    setBulkImporting(true);
    setBulkProgress({ current: 0, total: validRows.length, success: 0, failed: 0 });

    let success = 0;
    let failed = 0;

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      try {
        const productData = {
          name: row.name.trim(),
          sku: row.sku.trim(),
          description: row.description?.trim() || '',
          category: row.category?.trim() || '',
          price: parseFloat(row.price),
          cost: parseFloat(row.cost) || 0,
          quantityInStock: parseInt(row.quantityinstock) || 0,
          quantityInShop: parseInt(row.quantityinshop) || 0,
          unit: row.unit?.trim() || 'pcs',
          active: true,
          companyId: parseInt(selectedCompanyId)
        };

        await apiService.createProduct(productData);
        success++;
      } catch (err) {
        failed++;
        console.error(`Failed to import row ${i + 1}:`, err);
      }

      setBulkProgress({ current: i + 1, total: validRows.length, success, failed });
    }

    setBulkImporting(false);

    if (failed === 0) {
      loadProducts();
      loadCategories();
      setCurrentView('list');
      setBulkData([]);
      setBulkErrors([]);
      setBulkFileName('');
    } else {
      setError(`Import completed: ${success} succeeded, ${failed} failed. Check console for details.`);
      loadProducts();
    }
  };

  // ============================
  // BULK RESTOCK FUNCTIONS
  // ============================
  const downloadRestockTemplate = () => {
    const headers = ['sku', 'name', 'currentStock', 'currentShop', 'currentStore', 'quantity', 'quantityToShop', 'restockDate', 'notes'];
    const today = new Date().toISOString().split('T')[0];
    const rows = products.map(p => {
      const shop = p.quantityInShop || 0;
      const store = (p.quantityInStock || 0) - shop;
      return [
        p.sku || '',
        p.name || '',
        p.quantityInStock || 0,
        shop,
        store,
        '',
        '',
        today,
        ''
      ];
    });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const companyName = companies.find(c => c.id === parseInt(selectedCompanyId))?.name || 'company';
    a.href = url;
    a.download = `restock_template_${companyName.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const validateRestockData = (rows) => {
    rows.forEach((row) => {
      const rowErrors = [];
      const sku = (row.sku || '').trim();
      const qty = row.quantity;

      // Match product by SKU
      const matchedProduct = products.find(p => p.sku?.toLowerCase() === sku.toLowerCase());

      if (!sku) {
        rowErrors.push('SKU is required');
      } else if (!matchedProduct) {
        rowErrors.push('SKU not found in system');
      }

      if (!qty || isNaN(parseInt(qty)) || parseInt(qty) <= 0) {
        rowErrors.push('Quantity must be a positive number');
      }

      const restockDate = (row.restockdate || row.restockDate || '').trim();
      if (restockDate && isNaN(Date.parse(restockDate))) {
        rowErrors.push('Invalid date format');
      }

      // Validate quantityToShop
      const toShop = parseInt(row.quantitytoshop || row.quantityToShop || 0) || 0;
      const totalQty = parseInt(qty) || 0;
      if (toShop < 0) {
        rowErrors.push('Qty to shop cannot be negative');
      } else if (toShop > totalQty) {
        rowErrors.push('Qty to shop cannot exceed total quantity');
      }

      row._matchedProduct = matchedProduct || null;
      row._errors = rowErrors;
      row._valid = rowErrors.length === 0;
    });

    return rows;
  };

  const handleRestockFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setRestockFileName(file.name);
    setError('');

    const isCSV = file.name.toLowerCase().endsWith('.csv');
    const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');

    if (!isCSV && !isExcel) {
      setError('Please upload a CSV or Excel (.xlsx) file');
      return;
    }

    if (isCSV) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const rows = parseCSV(event.target.result);
          if (rows.length === 0) {
            setError('No data rows found in file');
            return;
          }
          // Filter out rows with no quantity entered
          const filledRows = rows.filter(r => r.quantity && String(r.quantity).trim() !== '');
          if (filledRows.length === 0) {
            setError('No rows with restock quantity found. Fill in the "quantity" column.');
            return;
          }
          const validated = validateRestockData(filledRows);
          setRestockData(validated);
          setCurrentView('bulkRestock');
        } catch (err) {
          setError('Failed to parse CSV file: ' + err.message);
        }
      };
      reader.readAsText(file);
    } else if (isExcel) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const XLSX = await import('xlsx');
          const workbook = XLSX.read(event.target.result, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

          const rows = jsonData.map((row, i) => {
            const normalized = {};
            Object.keys(row).forEach(key => {
              normalized[key.toLowerCase().trim()] = String(row[key]);
            });
            normalized._rowNum = i + 2;
            return normalized;
          });

          const filledRows = rows.filter(r => r.quantity && String(r.quantity).trim() !== '');
          if (filledRows.length === 0) {
            setError('No rows with restock quantity found. Fill in the "quantity" column.');
            return;
          }
          const validated = validateRestockData(filledRows);
          setRestockData(validated);
          setCurrentView('bulkRestock');
        } catch (err) {
          setError('Failed to parse Excel file. Make sure "xlsx" package is installed.');
          console.error(err);
        }
      };
      reader.readAsArrayBuffer(file);
    }

    e.target.value = '';
  };

  const handleRemoveRestockRow = (index) => {
    const updated = restockData.filter((_, i) => i !== index);
    const revalidated = validateRestockData(updated);
    setRestockData(revalidated);
  };

  const handleBulkRestock = async () => {
    const validRows = restockData.filter(r => r._valid);
    if (validRows.length === 0) {
      setError('No valid rows to restock');
      return;
    }

    setRestockImporting(true);
    setRestockProgress({ current: 0, total: validRows.length, success: 0, failed: 0 });

    let success = 0;
    let failed = 0;

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      try {
        const restockDate = (row.restockdate || row.restockDate || new Date().toISOString().split('T')[0]).trim();
        const replenishmentData = {
          quantity: parseInt(row.quantity),
          quantityToShop: parseInt(row.quantitytoshop || row.quantityToShop || 0) || 0,
          restockDate: restockDate + 'T00:00:00',
          notes: (row.notes || '').trim() || `Bulk restock - ${new Date().toLocaleDateString()}`,
          companyId: parseInt(selectedCompanyId)
        };

        await apiService.replenishProductStock(row._matchedProduct.id, replenishmentData);
        success++;
      } catch (err) {
        failed++;
        console.error(`Failed to restock ${row.sku}:`, err);
      }

      setRestockProgress({ current: i + 1, total: validRows.length, success, failed });
    }

    setRestockImporting(false);

    if (failed === 0) {
      loadProducts();
      setCurrentView('list');
      setRestockData([]);
      setRestockFileName('');
    } else {
      setError(`Restock completed: ${success} succeeded, ${failed} failed.`);
      loadProducts();
    }
  };

  // ============================
  // BULK TRANSFER TO SHOP FUNCTIONS
  // ============================
  const downloadTransferTemplate = () => {
    const headers = ['sku', 'name', 'totalStock', 'currentShop', 'currentStore', 'transferToShop', 'notes'];
    const rows = products
      .filter(p => {
        const shop = p.quantityInShop || 0;
        const store = (p.quantityInStock || 0) - shop;
        return store > 0; // Only include products that have store stock
      })
      .map(p => {
        const shop = p.quantityInShop || 0;
        const store = (p.quantityInStock || 0) - shop;
        return [
          p.sku || '',
          p.name || '',
          p.quantityInStock || 0,
          shop,
          store,
          '',
          ''
        ];
      });

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const companyName = companies.find(c => c.id === parseInt(selectedCompanyId))?.name || 'company';
    a.href = url;
    a.download = `transfer_template_${companyName.replace(/\s+/g, '_')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const validateTransferData = (rows) => {
    rows.forEach((row) => {
      const rowErrors = [];
      const sku = (row.sku || '').trim();
      const qty = row.transfertoshop || row.transferToShop;

      // Match product by SKU
      const matchedProduct = products.find(p => p.sku?.toLowerCase() === sku.toLowerCase());

      if (!sku) {
        rowErrors.push('SKU is required');
      } else if (!matchedProduct) {
        rowErrors.push('SKU not found in system');
      }

      const transferQty = parseInt(qty) || 0;
      if (!qty || isNaN(parseInt(qty)) || transferQty <= 0) {
        rowErrors.push('Transfer quantity must be a positive number');
      }

      // Validate against available store stock
      if (matchedProduct && transferQty > 0) {
        const shopQty = matchedProduct.quantityInShop || 0;
        const storeQty = (matchedProduct.quantityInStock || 0) - shopQty;
        if (transferQty > storeQty) {
          rowErrors.push(`Only ${storeQty} available in store/warehouse`);
        }
      }

      row._matchedProduct = matchedProduct || null;
      row._errors = rowErrors;
      row._valid = rowErrors.length === 0;
    });

    return rows;
  };

  const handleTransferFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setTransferFileName(file.name);
    setError('');

    const isCSV = file.name.toLowerCase().endsWith('.csv');
    const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');

    if (!isCSV && !isExcel) {
      setError('Please upload a CSV or Excel (.xlsx) file');
      return;
    }

    const processRows = (rows) => {
      const filledRows = rows.filter(r => {
        const qty = r.transfertoshop || r.transferToShop || r.transfertoshop;
        return qty && String(qty).trim() !== '';
      });
      if (filledRows.length === 0) {
        setError('No rows with transfer quantity found. Fill in the "transferToShop" column.');
        return;
      }
      const validated = validateTransferData(filledRows);
      setTransferData(validated);
      setCurrentView('bulkTransfer');
    };

    if (isCSV) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const rows = parseCSV(event.target.result);
          if (rows.length === 0) {
            setError('No data rows found in file');
            return;
          }
          processRows(rows);
        } catch (err) {
          setError('Failed to parse CSV file: ' + err.message);
        }
      };
      reader.readAsText(file);
    } else if (isExcel) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const XLSX = await import('xlsx');
          const workbook = XLSX.read(event.target.result, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

          const rows = jsonData.map((row, i) => {
            const normalized = {};
            Object.keys(row).forEach(key => {
              normalized[key.toLowerCase().trim()] = String(row[key]);
            });
            normalized._rowNum = i + 2;
            return normalized;
          });

          processRows(rows);
        } catch (err) {
          setError('Failed to parse Excel file. Make sure "xlsx" package is installed.');
          console.error(err);
        }
      };
      reader.readAsArrayBuffer(file);
    }

    e.target.value = '';
  };

  const handleRemoveTransferRow = (index) => {
    const updated = transferData.filter((_, i) => i !== index);
    const revalidated = validateTransferData(updated);
    setTransferData(revalidated);
  };

  const handleBulkTransfer = async () => {
    const validRows = transferData.filter(r => r._valid);
    if (validRows.length === 0) {
      setError('No valid rows to transfer');
      return;
    }

    setTransferImporting(true);
    setTransferProgress({ current: 0, total: validRows.length, success: 0, failed: 0 });

    let success = 0;
    let failed = 0;

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      try {
        const transferQty = parseInt(row.transfertoshop || row.transferToShop || 0);
        await apiService.transferToShop(row._matchedProduct.id, {
          quantity: transferQty,
          notes: (row.notes || '').trim() || `Bulk transfer to shop - ${new Date().toLocaleDateString()}`,
          companyId: parseInt(selectedCompanyId)
        });
        success++;
      } catch (err) {
        failed++;
        console.error(`Failed to transfer ${row.sku}:`, err);
      }

      setTransferProgress({ current: i + 1, total: validRows.length, success, failed });
    }

    setTransferImporting(false);

    if (failed === 0) {
      loadProducts();
      setCurrentView('list');
      setTransferData([]);
      setTransferFileName('');
    } else {
      setError(`Transfer completed: ${success} succeeded, ${failed} failed.`);
      loadProducts();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  // Product List View
  if (currentView === 'list') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Product Management</h1>
            <p className="text-gray-600">Manage your product inventory and stock movements</p>
          </div>
          <div className="mt-4 sm:mt-0 flex flex-wrap gap-2">
            <button
              onClick={downloadTemplate}
              className="bg-green-600 text-white px-3 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
            >
              <Download className="w-4 h-4" />
              <span>Product Template</span>
            </button>
            <label className="bg-orange-600 text-white px-3 py-2 rounded-lg hover:bg-orange-700 transition-colors flex items-center space-x-1 cursor-pointer text-sm">
              <Upload className="w-4 h-4" />
              <span>Bulk Add</span>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileUpload}
                className="hidden"
              />
            </label>
            <button
              onClick={downloadRestockTemplate}
              className="bg-teal-600 text-white px-3 py-2 rounded-lg hover:bg-teal-700 transition-colors flex items-center space-x-1 text-sm"
            >
              <Download className="w-4 h-4" />
              <span>Restock Template</span>
            </button>
            <label className="bg-amber-600 text-white px-3 py-2 rounded-lg hover:bg-amber-700 transition-colors flex items-center space-x-1 cursor-pointer text-sm">
              <TrendingUp className="w-4 h-4" />
              <span>Bulk Restock</span>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleRestockFileUpload}
                className="hidden"
              />
            </label>
            <button
              onClick={downloadTransferTemplate}
              className="bg-cyan-600 text-white px-3 py-2 rounded-lg hover:bg-cyan-700 transition-colors flex items-center space-x-1 text-sm"
            >
              <Download className="w-4 h-4" />
              <span>Transfer Template</span>
            </button>
            <label className="bg-violet-600 text-white px-3 py-2 rounded-lg hover:bg-violet-700 transition-colors flex items-center space-x-1 cursor-pointer text-sm">
              <ArrowRightLeft className="w-4 h-4" />
              <span>Bulk Transfer</span>
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleTransferFileUpload}
                className="hidden"
              />
            </label>
            <button
              onClick={handleCreate}
              className="bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition-colors flex items-center space-x-1 text-sm"
            >
              <Plus className="w-4 h-4" />
              <span>Add Product</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Company Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
              <div className="flex items-center space-x-2">
                <Building2 className="w-4 h-4 text-gray-400" />
                <select
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">All Categories</option>
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('');
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Stock</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shop</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Store</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentProduct.map((product) => {
                  const stockStatus = getStockStatus(product.quantityInStock);
                  return (
                    <tr key={product.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                              <Package className="h-5 w-5 text-indigo-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{product.name}</div>
                            <div className="text-sm text-gray-500 truncate max-w-xs">{product.description}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.sku}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{product.category}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(product.price)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-900 mr-2">{product.quantityInStock}</span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${stockStatus.color}`}>
                            {stockStatus.label}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-blue-700 bg-blue-50 px-2 py-0.5 rounded">{product.quantityInShop || 0}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded">{(product.quantityInStock || 0) - (product.quantityInShop || 0)}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          product.active 
                            ? 'text-green-800 bg-green-100' 
                            : 'text-red-800 bg-red-100'
                        }`}>
                          {product.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                       <div className="flex items-center space-x-2">
                          {user?.role === 'ADMIN' && (
                            <>
                              <button
                                onClick={() => handleView(product)}
                                className="text-indigo-600 hover:text-indigo-900"
                                title="View Product"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleEdit(product)}
                                className="text-blue-600 hover:text-blue-900"
                                title="Edit Product"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleReplenishStock(product)}
                                className="text-green-600 hover:text-green-900"
                                title="Replenish Stock"
                              >
                                <TrendingUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleTransferToShop(product)}
                                className="text-teal-600 hover:text-teal-900"
                                title="Transfer to Shop"
                              >
                                <ArrowRightLeft className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleViewMovements(product)}
                                className="text-purple-600 hover:text-purple-900"
                                title="Stock Movement History"
                              >
                                <History className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(product.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Delete Product"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(indexOfLastItem, filteredProducts.length)}</span> of{' '}
                      <span className="font-medium">{filteredProducts.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setCurrentPage(Math.max(currentPage - 1, 1))}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      
                      {[...Array(totalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        const isCurrentPage = pageNumber === currentPage;
                        
                        if (
                          pageNumber === 1 ||
                          pageNumber === totalPages ||
                          (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                        ) {
                          return (
                            <button
                              key={pageNumber}
                              onClick={() => setCurrentPage(pageNumber)}
                              className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                isCurrentPage
                                  ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                                  : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                            >
                              {pageNumber}
                            </button>
                          );
                        } else if (
                          pageNumber === currentPage - 2 ||
                          pageNumber === currentPage + 2
                        ) {
                          return (
                            <span key={pageNumber} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          );
                        }
                        return null;
                      })}
                      
                      <button
                        onClick={() => setCurrentPage(Math.min(currentPage + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>

          {currentProduct.length === 0 && (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No products found</h3>
              <p className="mt-1 text-sm text-gray-500">Get started by creating a new product.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Create/Edit Product Form View
  if (currentView === 'create' || currentView === 'edit') {
    return (
      <div className="space-y-6">
        {/* Header with Back Button */}
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {currentView === 'create' ? 'Add New Product' : 'Edit Product'}
            </h1>
            <p className="text-gray-600">
              {currentView === 'create' ? 'Fill in the details to create a new product' : 'Update product information'}
            </p>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Product Information</h3>
            <div className="space-y-4">
              {/* Company Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Company *</label>
                <select
                  required
                  value={formData.companyId}
                  onChange={(e) => setFormData({...formData, companyId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                >
                  <option value="">Select Company</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>{company.name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
                  <input
                    type="text"
                    required
                    value={formData.sku}
                    onChange={(e) => setFormData({...formData, sku: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cost *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={formData.cost}
                    onChange={(e) => setFormData({...formData, cost: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Total Stock Quantity</label>
                  <input
                    type="number"
                    value={formData.quantityInStock}
                    onChange={(e) => setFormData({...formData, quantityInStock: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Qty in Shop</label>
                  <input
                    type="number"
                    value={formData.quantityInShop}
                    onChange={(e) => {
                      const shopVal = parseInt(e.target.value) || 0;
                      const totalVal = parseInt(formData.quantityInStock) || 0;
                      if (shopVal > totalVal) {
                        setError('Qty in Shop cannot exceed Total Stock');
                      } else {
                        setError('');
                      }
                      setFormData({...formData, quantityInShop: e.target.value});
                    }}
                    className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Qty in Store/Warehouse</label>
                  <div className="w-full px-3 py-2 border border-amber-300 rounded-lg bg-amber-50 text-amber-800 font-medium">
                    {Math.max(0, (parseInt(formData.quantityInStock) || 0) - (parseInt(formData.quantityInShop) || 0))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                  <input
                    type="text"
                    value={formData.unit}
                    onChange={(e) => setFormData({...formData, unit: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="active"
                  checked={formData.active}
                  onChange={(e) => setFormData({...formData, active: e.target.checked})}
                  className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                />
                <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                  Active
                </label>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 sm:flex-none px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {submitting ? 'Saving...' : (currentView === 'create' ? 'Create Product' : 'Update Product')}
            </button>
            <button
              type="button"
              onClick={handleBackToList}
              disabled={submitting}
              className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  // View Product Details
  if (currentView === 'view') {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Product Details</h1>
            <p className="text-gray-600">{selectedProduct?.name}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="space-y-6">
            {/* Company info */}
            <div>
              <label className="block text-sm font-medium text-gray-700">Company</label>
              <p className="mt-1 text-sm text-gray-900">
                {getCompanyName(selectedProduct?.companyId) || 'N/A'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Name</label>
                <p className="mt-1 text-sm text-gray-900">{selectedProduct?.name}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">SKU</label>
                <p className="mt-1 text-sm text-gray-900">{selectedProduct?.sku}</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <p className="mt-1 text-sm text-gray-900">{selectedProduct?.description || 'No description'}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Price</label>
                <p className="mt-1 text-sm text-gray-900">{formatCurrency(selectedProduct?.price)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Cost</label>
                <p className="mt-1 text-sm text-gray-900">{formatCurrency(selectedProduct?.cost)}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700">Total Stock</label>
                <p className="mt-1 text-sm font-bold text-gray-900">{selectedProduct?.quantityInStock}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700">In Shop</label>
                <p className="mt-1 text-sm font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded inline-block">{selectedProduct?.quantityInShop || 0}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-700">In Store/Warehouse</label>
                <p className="mt-1 text-sm font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded inline-block">{(selectedProduct?.quantityInStock || 0) - (selectedProduct?.quantityInShop || 0)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Category</label>
                <p className="mt-1 text-sm text-gray-900">{selectedProduct?.category || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Unit</label>
                <p className="mt-1 text-sm text-gray-900">{selectedProduct?.unit || 'N/A'}</p>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <p className="mt-1">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  selectedProduct?.active 
                    ? 'text-green-800 bg-green-100' 
                    : 'text-red-800 bg-red-100'
                }`}>
                  {selectedProduct?.active ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Stock Replenishment View
  if (currentView === 'stock') {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Replenish Stock</h1>
            <p className="text-gray-600">{selectedProduct?.name}{(selectedProduct?.description || selectedProduct?.unit) ? ` (${selectedProduct.description || selectedProduct.unit})` : ''}{selectedProduct?.sku ? ` | SKU: ${selectedProduct.sku}` : ''} - Total: {selectedProduct?.quantityInStock} | Shop: {selectedProduct?.quantityInShop || 0} | Store: {(selectedProduct?.quantityInStock || 0) - (selectedProduct?.quantityInShop || 0)}</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <form onSubmit={handleStockReplenishment} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Quantity to Add *</label>
                <input
                  type="number"
                  min="1"
                  required
                  value={stockForm.quantity}
                  onChange={(e) => setStockForm({...stockForm, quantity: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">Of which, Qty to Shop</label>
                <input
                  type="number"
                  min="0"
                  value={stockForm.quantityToShop}
                  onChange={(e) => {
                    const toShop = parseInt(e.target.value) || 0;
                    const total = parseInt(stockForm.quantity) || 0;
                    if (toShop > total) {
                      setError('Qty to Shop cannot exceed total quantity');
                    } else {
                      setError('');
                    }
                    setStockForm({...stockForm, quantityToShop: e.target.value});
                  }}
                  className="w-full px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50"
                  placeholder="0 = all go to store"
                />
                <p className="text-xs text-gray-500 mt-1">
                  → {parseInt(stockForm.quantityToShop) || 0} to shop, {Math.max(0, (parseInt(stockForm.quantity) || 0) - (parseInt(stockForm.quantityToShop) || 0))} to store
                </p>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Restock Date *</label>
              <input
                type="date"
                required
                value={stockForm.replenishDate}
                onChange={(e) => setStockForm({...stockForm, replenishDate: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                rows={3}
                value={stockForm.notes}
                onChange={(e) => setStockForm({...stockForm, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="Reason for stock replenishment..."
              />
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                type="submit"
                disabled={submitting}
                className="flex-1 sm:flex-none px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {submitting ? 'Replenishing...' : 'Replenish Stock'}
              </button>
              <button
                type="button"
                onClick={handleBackToList}
                disabled={submitting}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Transfer to Shop View
  if (currentView === 'transfer') {
    const currentShop = selectedProduct?.quantityInShop || 0;
    const currentStore = (selectedProduct?.quantityInStock || 0) - currentShop;
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Transfer to Shop</h1>
            <p className="text-gray-600">{selectedProduct?.name}{(selectedProduct?.description || selectedProduct?.unit) ? ` (${selectedProduct.description || selectedProduct.unit})` : ''}{selectedProduct?.sku ? ` | SKU: ${selectedProduct.sku}` : ''}</p>
          </div>
        </div>

        {/* Stock Breakdown Cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-50 border rounded-lg p-4 text-center">
            <p className="text-sm text-gray-500">Total Stock</p>
            <p className="text-2xl font-bold text-gray-900">{selectedProduct?.quantityInStock || 0}</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
            <p className="text-sm text-blue-600">In Shop</p>
            <p className="text-2xl font-bold text-blue-700">{currentShop}</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
            <p className="text-sm text-amber-600">In Store/Warehouse</p>
            <p className="text-2xl font-bold text-amber-700">{currentStore}</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <form onSubmit={handleSubmitTransfer} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity to Move to Shop *</label>
              <input
                type="number"
                min="1"
                max={currentStore}
                required
                value={transferForm.quantity}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || 0;
                  if (val > currentStore) {
                    setError(`Cannot transfer more than ${currentStore} units (available in store)`);
                  } else {
                    setError('');
                  }
                  setTransferForm({...transferForm, quantity: e.target.value});
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder={`Max: ${currentStore}`}
              />
              {transferForm.quantity && !error && (
                <div className="mt-2 p-3 bg-teal-50 border border-teal-200 rounded-lg">
                  <p className="text-sm text-teal-800">
                    After transfer: <span className="font-bold">Shop: {currentShop + (parseInt(transferForm.quantity) || 0)}</span> | <span className="font-bold">Store: {currentStore - (parseInt(transferForm.quantity) || 0)}</span>
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                rows={3}
                value={transferForm.notes}
                onChange={(e) => setTransferForm({...transferForm, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                placeholder="Reason for transfer..."
              />
            </div>

            <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
              <button
                type="submit"
                disabled={submitting || !!error || !transferForm.quantity}
                className="flex-1 sm:flex-none px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                {submitting ? 'Transferring...' : 'Transfer to Shop'}
              </button>
              <button
                type="button"
                onClick={handleBackToList}
                className="flex-1 sm:flex-none px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  // Stock Movements View
  if (currentView === 'movements') {
    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button
            onClick={handleBackToList}
            className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stock Movement History</h1>
            <p className="text-gray-600">{selectedProduct?.name} - Total: {selectedProduct?.quantityInStock} | Shop: {selectedProduct?.quantityInShop || 0} | Store: {(selectedProduct?.quantityInStock || 0) - (selectedProduct?.quantityInShop || 0)}</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Restock Date</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stock Before</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Stock After</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-blue-600 uppercase">Shop Before</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-blue-600 uppercase">Shop After</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reference</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {stockMovements.map((movement) => (
                  <tr key={movement.id} className={movement.type === 'TRANSFER_TO_SHOP' ? 'bg-teal-50' : ''}>
                    <td className="px-3 py-2 text-sm text-gray-900">{formatDateTime(movement.createdAt)}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{movement.restockDate ? new Date(movement.restockDate).toLocaleDateString() : '-'}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getMovementTypeColor(movement.type)}`}>
                        {movement.type === 'TRANSFER_TO_SHOP' ? 'TRANSFER→SHOP' : movement.type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm">
                      <span className={movement.quantity > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                        {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-sm text-gray-900">{movement.stockBefore}</td>
                    <td className="px-3 py-2 text-sm text-gray-900">{movement.stockAfter}</td>
                    <td className="px-3 py-2 text-sm text-blue-700">{movement.shopBefore != null ? movement.shopBefore : '-'}</td>
                    <td className="px-3 py-2 text-sm text-blue-700 font-medium">{movement.shopAfter != null ? movement.shopAfter : '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{movement.reference || '-'}</td>
                    <td className="px-3 py-2 text-sm text-gray-600">{movement.createdByName}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {stockMovements.length === 0 && (
            <div className="text-center py-8">
              <History className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-2 text-sm text-gray-500">No stock movements recorded</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Bulk Upload Preview View
  if (currentView === 'bulkPreview') {
    const validCount = bulkData.filter(r => r._valid).length;
    const errorCount = bulkData.filter(r => !r._valid).length;

    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={() => { setCurrentView('list'); setBulkData([]); setBulkErrors([]); setBulkFileName(''); }} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bulk Upload Preview</h1>
            <p className="text-gray-600">Review and import products from: {bulkFileName}</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <FileSpreadsheet className="w-8 h-8 text-blue-500" />
              <div>
                <div className="text-sm text-gray-500">Total Rows</div>
                <div className="text-xl font-bold text-gray-900">{bulkData.length}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <div className="text-sm text-gray-500">Valid</div>
                <div className="text-xl font-bold text-green-600">{validCount}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <XCircle className="w-8 h-8 text-red-500" />
              <div>
                <div className="text-sm text-gray-500">Errors</div>
                <div className="text-xl font-bold text-red-600">{errorCount}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <Building2 className="w-8 h-8 text-indigo-500" />
              <div>
                <div className="text-sm text-gray-500">Company</div>
                <div className="text-sm font-bold text-gray-900">{companies.find(c => c.id === parseInt(selectedCompanyId))?.name}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Import Progress */}
        {bulkImporting && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Importing Products...</h3>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div
                className="bg-indigo-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>{bulkProgress.current} of {bulkProgress.total}</span>
              <span className="space-x-3">
                <span className="text-green-600">{bulkProgress.success} success</span>
                {bulkProgress.failed > 0 && <span className="text-red-600">{bulkProgress.failed} failed</span>}
              </span>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-blue-600 uppercase">In Shop</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Unit</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bulkData.map((row, index) => (
                  <tr key={index} className={row._valid ? 'hover:bg-gray-50' : 'bg-red-50'}>
                    <td className="px-3 py-3 text-sm text-gray-500">{row._rowNum || index + 1}</td>
                    <td className="px-3 py-3">
                      {row._valid ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : (
                        <div className="flex items-center">
                          <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                          <div className="ml-2">
                            {row._errors.map((err, i) => (
                              <div key={i} className="text-xs text-red-600">{err}</div>
                            ))}
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-sm font-medium text-gray-900">{row.name || '-'}</td>
                    <td className="px-3 py-3 text-sm text-gray-900">{row.sku || '-'}</td>
                    <td className="px-3 py-3 text-sm text-gray-900">{row.category || '-'}</td>
                    <td className="px-3 py-3 text-sm text-gray-900">{row.price || '-'}</td>
                    <td className="px-3 py-3 text-sm text-gray-900">{row.cost || '-'}</td>
                    <td className="px-3 py-3 text-sm text-gray-900">{row.quantityinstock || row.quantityInStock || '-'}</td>
                    <td className="px-3 py-3 text-sm text-blue-700 font-medium">{row.quantityinshop || row.quantityInShop || '0'}</td>
                    <td className="px-3 py-3 text-sm text-gray-900">{row.unit || 'pcs'}</td>
                    <td className="px-3 py-3">
                      <button
                        onClick={() => handleRemoveBulkRow(index)}
                        className="text-red-600 hover:text-red-800"
                        title="Remove row"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {bulkData.length === 0 && (
            <div className="text-center py-12">
              <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No data to preview</h3>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <button
            onClick={handleBulkImport}
            disabled={bulkImporting || validCount === 0}
            className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <Upload className="w-4 h-4" />
            <span>{bulkImporting ? `Importing ${bulkProgress.current}/${bulkProgress.total}...` : `Import ${validCount} Valid Products`}</span>
          </button>
          <label className="px-6 py-3 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 transition-colors cursor-pointer flex items-center space-x-2">
            <FileSpreadsheet className="w-4 h-4" />
            <span>Upload Different File</span>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
            />
          </label>
          <button
            onClick={() => { setCurrentView('list'); setBulkData([]); setBulkErrors([]); setBulkFileName(''); }}
            disabled={bulkImporting}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Bulk Restock Preview View
  if (currentView === 'bulkRestock') {
    const validCount = restockData.filter(r => r._valid).length;
    const errorCount = restockData.filter(r => !r._valid).length;
    const totalQty = restockData.filter(r => r._valid).reduce((sum, r) => sum + parseInt(r.quantity || 0), 0);
    const totalToShop = restockData.filter(r => r._valid).reduce((sum, r) => sum + (parseInt(r.quantitytoshop || r.quantityToShop || 0) || 0), 0);

    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={() => { setCurrentView('list'); setRestockData([]); setRestockFileName(''); }} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bulk Restock Preview</h1>
            <p className="text-gray-600">Review and restock products from: {restockFileName}</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <FileSpreadsheet className="w-8 h-8 text-blue-500" />
              <div>
                <div className="text-sm text-gray-500">Total Rows</div>
                <div className="text-xl font-bold text-gray-900">{restockData.length}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <div className="text-sm text-gray-500">Valid</div>
                <div className="text-xl font-bold text-green-600">{validCount}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <XCircle className="w-8 h-8 text-red-500" />
              <div>
                <div className="text-sm text-gray-500">Errors</div>
                <div className="text-xl font-bold text-red-600">{errorCount}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <TrendingUp className="w-8 h-8 text-teal-500" />
              <div>
                <div className="text-sm text-gray-500">Total Qty to Add</div>
                <div className="text-xl font-bold text-teal-600">{totalQty.toLocaleString()}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <ArrowRightLeft className="w-8 h-8 text-blue-500" />
              <div>
                <div className="text-sm text-blue-600">To Shop</div>
                <div className="text-xl font-bold text-blue-700">{totalToShop.toLocaleString()}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <Building2 className="w-8 h-8 text-indigo-500" />
              <div>
                <div className="text-sm text-gray-500">Company</div>
                <div className="text-sm font-bold text-gray-900">{companies.find(c => c.id === parseInt(selectedCompanyId))?.name}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Import Progress */}
        {restockImporting && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Restocking Products...</h3>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div
                className="bg-teal-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${restockProgress.total > 0 ? (restockProgress.current / restockProgress.total) * 100 : 0}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>{restockProgress.current} of {restockProgress.total}</span>
              <span className="space-x-3">
                <span className="text-green-600">{restockProgress.success} success</span>
                {restockProgress.failed > 0 && <span className="text-red-600">{restockProgress.failed} failed</span>}
              </span>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Current Stock</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-blue-600 uppercase">Cur. Shop</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Add Qty</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-blue-600 uppercase">To Shop</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">New Stock</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Restock Date</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {restockData.map((row, index) => {
                  const currentStock = row._matchedProduct?.quantityInStock || 0;
                  const currentShop = row._matchedProduct?.quantityInShop || 0;
                  const addQty = parseInt(row.quantity) || 0;
                  const toShop = parseInt(row.quantitytoshop || row.quantityToShop || 0) || 0;
                  const newStock = currentStock + addQty;

                  return (
                    <tr key={index} className={row._valid ? 'hover:bg-gray-50' : 'bg-red-50'}>
                      <td className="px-3 py-3 text-sm text-gray-500">{row._rowNum || index + 1}</td>
                      <td className="px-3 py-3">
                        {row._valid ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <div className="flex items-center">
                            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                            <div className="ml-2">
                              {row._errors.map((err, i) => (
                                <div key={i} className="text-xs text-red-600">{err}</div>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm font-mono text-gray-900">{row.sku || '-'}</td>
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">
                        {row._matchedProduct?.name || <span className="text-red-500 italic">Not found</span>}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">{row._matchedProduct ? currentStock : '-'}</td>
                      <td className="px-3 py-3 text-sm text-blue-700">{row._matchedProduct ? currentShop : '-'}</td>
                      <td className="px-3 py-3 text-sm font-bold text-teal-700">+{addQty}</td>
                      <td className="px-3 py-3 text-sm font-bold text-blue-700">{toShop > 0 ? `+${toShop}` : '-'}</td>
                      <td className="px-3 py-3 text-sm font-bold text-indigo-700">{row._matchedProduct ? newStock : '-'}</td>
                      <td className="px-3 py-3 text-sm text-gray-900">{row.restockdate || row.restockDate || '-'}</td>
                      <td className="px-3 py-3 text-sm text-gray-500 max-w-[150px] truncate">{row.notes || '-'}</td>
                      <td className="px-3 py-3">
                        <button
                          onClick={() => handleRemoveRestockRow(index)}
                          className="text-red-600 hover:text-red-800"
                          title="Remove row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {restockData.length === 0 && (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No restock data to preview</h3>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <button
            onClick={handleBulkRestock}
            disabled={restockImporting || validCount === 0}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <TrendingUp className="w-4 h-4" />
            <span>{restockImporting ? `Restocking ${restockProgress.current}/${restockProgress.total}...` : `Restock ${validCount} Products (+${totalQty.toLocaleString()} units)`}</span>
          </button>
          <label className="px-6 py-3 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer flex items-center space-x-2">
            <FileSpreadsheet className="w-4 h-4" />
            <span>Upload Different File</span>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleRestockFileUpload}
              className="hidden"
            />
          </label>
          <button
            onClick={() => { setCurrentView('list'); setRestockData([]); setRestockFileName(''); }}
            disabled={restockImporting}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // Bulk Transfer to Shop Preview View
  if (currentView === 'bulkTransfer') {
    const validCount = transferData.filter(r => r._valid).length;
    const errorCount = transferData.filter(r => !r._valid).length;
    const totalTransferQty = transferData.filter(r => r._valid).reduce((sum, r) => sum + (parseInt(r.transfertoshop || r.transferToShop || 0)), 0);

    return (
      <div className="space-y-6">
        <div className="flex items-center space-x-4">
          <button onClick={() => { setCurrentView('list'); setTransferData([]); setTransferFileName(''); }} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bulk Transfer to Shop Preview</h1>
            <p className="text-gray-600">Review and transfer products from store/warehouse to shop: {transferFileName}</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-400 mr-2" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <FileSpreadsheet className="w-8 h-8 text-blue-500" />
              <div>
                <div className="text-sm text-gray-500">Total Rows</div>
                <div className="text-xl font-bold text-gray-900">{transferData.length}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <CheckCircle className="w-8 h-8 text-green-500" />
              <div>
                <div className="text-sm text-gray-500">Valid</div>
                <div className="text-xl font-bold text-green-600">{validCount}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <XCircle className="w-8 h-8 text-red-500" />
              <div>
                <div className="text-sm text-gray-500">Errors</div>
                <div className="text-xl font-bold text-red-600">{errorCount}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <ArrowRightLeft className="w-8 h-8 text-violet-500" />
              <div>
                <div className="text-sm text-gray-500">Total to Transfer</div>
                <div className="text-xl font-bold text-violet-600">{totalTransferQty.toLocaleString()}</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border p-4">
            <div className="flex items-center space-x-3">
              <Building2 className="w-8 h-8 text-indigo-500" />
              <div>
                <div className="text-sm text-gray-500">Company</div>
                <div className="text-sm font-bold text-gray-900">{companies.find(c => c.id === parseInt(selectedCompanyId))?.name}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Import Progress */}
        {transferImporting && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Transferring to Shop...</h3>
            <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
              <div
                className="bg-violet-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${transferProgress.total > 0 ? (transferProgress.current / transferProgress.total) * 100 : 0}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>{transferProgress.current} of {transferProgress.total}</span>
              <span className="space-x-3">
                <span className="text-green-600">{transferProgress.success} success</span>
                {transferProgress.failed > 0 && <span className="text-red-600">{transferProgress.failed} failed</span>}
              </span>
            </div>
          </div>
        )}

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product Name</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Stock</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-blue-600 uppercase">Shop</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-amber-600 uppercase">Store</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-violet-600 uppercase">Transfer Qty</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-blue-600 uppercase">New Shop</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-amber-600 uppercase">New Store</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transferData.map((row, index) => {
                  const product = row._matchedProduct;
                  const currentShop = product?.quantityInShop || 0;
                  const currentStore = (product?.quantityInStock || 0) - currentShop;
                  const transferQty = parseInt(row.transfertoshop || row.transferToShop || 0);
                  const newShop = currentShop + transferQty;
                  const newStore = currentStore - transferQty;

                  return (
                    <tr key={index} className={row._valid ? 'hover:bg-gray-50' : 'bg-red-50'}>
                      <td className="px-3 py-3 text-sm text-gray-500">{row._rowNum || index + 1}</td>
                      <td className="px-3 py-3">
                        {row._valid ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <div className="flex items-center">
                            <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                            <div className="ml-2">
                              {row._errors.map((err, i) => (
                                <div key={i} className="text-xs text-red-600">{err}</div>
                              ))}
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 text-sm font-mono text-gray-900">{row.sku || '-'}</td>
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">
                        {product?.name || <span className="text-red-500 italic">Not found</span>}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">{product ? product.quantityInStock : '-'}</td>
                      <td className="px-3 py-3 text-sm">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">{product ? currentShop : '-'}</span>
                      </td>
                      <td className="px-3 py-3 text-sm">
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">{product ? currentStore : '-'}</span>
                      </td>
                      <td className="px-3 py-3 text-sm font-bold text-violet-700">→ {transferQty}</td>
                      <td className="px-3 py-3 text-sm">
                        {product ? (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-bold">{newShop}</span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3 text-sm">
                        {product ? (
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${newStore < 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{newStore}</span>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-500 max-w-[150px] truncate">{row.notes || '-'}</td>
                      <td className="px-3 py-3">
                        <button
                          onClick={() => handleRemoveTransferRow(index)}
                          className="text-red-600 hover:text-red-800"
                          title="Remove row"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {transferData.length === 0 && (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No transfer data to preview</h3>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <button
            onClick={handleBulkTransfer}
            disabled={transferImporting || validCount === 0}
            className="px-6 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <ArrowRightLeft className="w-4 h-4" />
            <span>{transferImporting ? `Transferring ${transferProgress.current}/${transferProgress.total}...` : `Transfer ${validCount} Products (${totalTransferQty.toLocaleString()} units → Shop)`}</span>
          </button>
          <label className="px-6 py-3 border border-amber-300 text-amber-700 rounded-lg hover:bg-amber-50 transition-colors cursor-pointer flex items-center space-x-2">
            <FileSpreadsheet className="w-4 h-4" />
            <span>Upload Different File</span>
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleTransferFileUpload}
              className="hidden"
            />
          </label>
          <button
            onClick={() => { setCurrentView('list'); setTransferData([]); setTransferFileName(''); }}
            disabled={transferImporting}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default ProductManagement;